import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');
const money = (n) => Number(n || 0).toLocaleString('en-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CURRENCY_OPTIONS = ['CHF', 'EUR', 'USD'];
/** Module-level in-flight map survives StrictMode remounts (avoids duplicate sync POSTs). */
const bexioSyncPaidInflight = new Map();

function todayDateInput() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseBexioDateField(v) {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : '';
}

function bexioDueDate(inv) {
  return parseBexioDateField(inv?.is_valid_to || inv?.is_valid_until);
}

function isBexioInvoiceChf(inv) {
  const code = String(inv?.currency_code || '').toUpperCase();
  if (code === 'CHF') return true;
  // Bexio commonly uses currency_id=1 for CHF when code lookup fails.
  return Number(inv?.currency_id) === 1;
}

/** Bexio invoice net (HT); falls back to `total` if `total_net` absent. */
function bexioNetTotal(inv) {
  const raw =
    inv?.total_net != null && String(inv.total_net).trim() !== ''
      ? inv.total_net
      : inv?.total != null && String(inv.total).trim() !== ''
        ? inv.total
        : '';
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/** Amount to log as paid: always Bexio net total. */
function bexioPaidLogAmount(inv) {
  return bexioNetTotal(inv);
}

function extractBexioInvoiceIdFromNote(note) {
  const m = String(note || '').match(/Bexio invoice #(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

function paymentsForTranche(trancheId, allPayments) {
  if (!trancheId) return [];
  return allPayments.filter((p) => p.tranche_id === trancheId);
}

export function DealInvoicesPaymentsTab({ deal, loading, onDealReload }) {
  const [payments, setPayments] = useState([]);
  const [tranches, setTranches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bexioPayload, setBexioPayload] = useState(null);
  const [bexioLoading, setBexioLoading] = useState(false);
  const [bexioInvoiceFilter, setBexioInvoiceFilter] = useState('');

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueInvoice, setIssueInvoice] = useState(null);
  const [issueForm, setIssueForm] = useState({ tranche_type: 'final' });

  const [paidOpen, setPaidOpen] = useState(false);
  const [paidInvoice, setPaidInvoice] = useState(null);
  const [paidForm, setPaidForm] = useState({ payment_date: '', note: '' });
  const [manualForm, setManualForm] = useState({ tranche_id: '', amount_chf: '', payment_date: todayDateInput(), note: '' });
  const [trancheFxOpen, setTrancheFxOpen] = useState(false);
  const [paymentFxOpen, setPaymentFxOpen] = useState(false);
  const [trancheFxTarget, setTrancheFxTarget] = useState(null);
  const [paymentFxTarget, setPaymentFxTarget] = useState(null);
  const [trancheFxForm, setTrancheFxForm] = useState({ source_currency: 'USD', source_amount: '', reason: '' });
  const [paymentFxForm, setPaymentFxForm] = useState({ source_currency: 'USD', source_amount: '', reason: '' });
  const [trancheFxPreview, setTrancheFxPreview] = useState(null);
  const [paymentFxPreview, setPaymentFxPreview] = useState(null);
  const [trancheFxPreviewLoading, setTrancheFxPreviewLoading] = useState(false);
  const [paymentFxPreviewLoading, setPaymentFxPreviewLoading] = useState(false);
  const [fxAuditRows, setFxAuditRows] = useState([]);

  const refreshPayments = useCallback(async () => {
    if (!deal?.id) return;
    const pRes = await api.getDealArPayments(deal.id);
    setPayments(pRes.data.rows || []);
  }, [deal?.id]);

  const refreshTranches = useCallback(async () => {
    if (!deal?.id) return;
    const tRes = await api.getDealTranches(deal.id);
    setTranches(tRes.data.rows || []);
    const promotedStage = tRes.data?.deal_stage;
    if (promotedStage && promotedStage !== deal.stage && onDealReload) {
      await onDealReload();
    }
  }, [deal?.id, deal?.stage, onDealReload]);

  const refreshFxAudit = useCallback(async () => {
    if (!deal?.id) return;
    const res = await api.getDealFxAudit(deal.id);
    setFxAuditRows(res.data || []);
  }, [deal?.id]);

  const loadBexioInvoices = useCallback(async () => {
    if (!deal?.id) return;
    const dealId = deal.id;
    setBexioLoading(true);
    let shared = bexioSyncPaidInflight.get(dealId);
    const ownsRequest = !shared;
    if (!shared) {
      // POST sync-paid: list + auto-post Bexio Paid into issued ledger (GET stays read-only).
      shared = api.syncBexioDealInvoicesPaid(dealId).finally(() => {
        bexioSyncPaidInflight.delete(dealId);
      });
      bexioSyncPaidInflight.set(dealId, shared);
    }
    try {
      const res = await shared;
      setBexioPayload(res.data);
      const synced = Number(res.data?.auto_synced_paid_count || 0);
      const failed = Number(res.data?.auto_sync_failed_count || 0);
      if (synced > 0 || failed > 0) {
        await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit()]);
        if (onDealReload) await onDealReload();
      }
      // Toasts only from the caller that started the shared request (StrictMode-safe).
      if (ownsRequest && synced > 0) {
        toast.success(
          synced === 1
            ? 'Bexio Paid synced into issued ledger'
            : `${synced} Bexio Paid invoices synced into issued ledger`
        );
      }
      if (ownsRequest && failed > 0) {
        toast.warning(
          failed === 1
            ? 'Could not auto-sync one Bexio Paid invoice into the ledger'
            : `Could not auto-sync ${failed} Bexio Paid invoices into the ledger`
        );
      }
    } catch (err) {
      setBexioPayload(null);
      if (ownsRequest) {
        const d = err.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Could not load Bexio invoices');
      }
    } finally {
      setBexioLoading(false);
    }
  }, [deal?.id, refreshPayments, refreshTranches, refreshFxAudit, onDealReload]);

  useEffect(() => {
    refreshPayments().catch(() => setPayments([]));
    refreshTranches().catch(() => setTranches([]));
    refreshFxAudit().catch(() => setFxAuditRows([]));
    loadBexioInvoices();
  }, [refreshPayments, refreshTranches, refreshFxAudit, loadBexioInvoices]);

  useEffect(() => {
    setBexioInvoiceFilter('');
  }, [deal?.id]);

  useEffect(() => {
    setManualForm({ tranche_id: '', amount_chf: '', payment_date: todayDateInput(), note: '' });
  }, [deal?.id]);

  const bexioInvoicesFiltered = useMemo(() => {
    const rows = bexioPayload?.invoices || [];
    const q = bexioInvoiceFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((inv) => {
      const doc = String(inv.document_nr || '').toLowerCase();
      const title = String(inv.title || '').toLowerCase();
      const idStr = String(inv.id || '');
      return doc.includes(q) || title.includes(q) || idStr.includes(q);
    });
  }, [bexioPayload?.invoices, bexioInvoiceFilter]);

  const issuedLedgerRows = useMemo(() => {
    const rows = bexioPayload?.invoices || [];
    // Show every tranche that has been posted to the customer ledger. Excluding
    // `partially_paid` / `overdue` used to leave those documents invisible in
    // this panel while still contributing to the Deal Library "Invoiced %"
    // numerator — making the KPI appear to disagree with the visible ledger.
    const billedStates = new Set(['issued', 'partially_paid', 'overdue', 'paid']);
    return rows
      .filter((inv) => billedStates.has(String(inv?.app_state || '')))
      .sort((a, b) => {
        const ad = String(a?.is_valid_from || '');
        const bd = String(b?.is_valid_from || '');
        return bd.localeCompare(ad);
      });
  }, [bexioPayload?.invoices]);

  const trancheById = useMemo(() => {
    const out = new Map();
    for (const t of tranches) out.set(t.tranche_id, t);
    return out;
  }, [tranches]);

  const manualPaymentTranches = useMemo(
    () => tranches.filter((t) => ['issued', 'partially_paid', 'overdue'].includes(String(t.status || '').toLowerCase())),
    [tranches]
  );

  const issuedLedgerDisplay = (inv) => {
    const row = trancheById.get(inv.app_tranche_id);
    if (!row) {
      return {
        amount: bexioNetTotal(inv),
        currency: 'CHF',
        fxAdjusted: false,
        sourceCurrency: null,
        outstandingChf: 0,
        paidChf: 0,
        hasMappedTranche: false,
      };
    }
    const sourceCurrency = row.source_currency || 'CHF';
    return {
      amount: Number(row.amount_chf || 0),
      currency: 'CHF',
      fxAdjusted: sourceCurrency !== 'CHF',
      sourceCurrency,
      outstandingChf: Number(row.outstanding_amount_chf || 0),
      paidChf: Number(row.paid_amount_chf || 0),
      hasMappedTranche: true,
    };
  };

  const openIssueModal = (inv) => {
    setIssueInvoice(inv);
    setIssueForm({ tranche_type: 'final' });
    setIssueOpen(true);
  };

  const openPaidModal = (inv) => {
    setPaidInvoice(inv);
    setPaidForm({
      payment_date: '',
      note: `Bexio invoice #${inv.id} ${inv.document_nr || ''} ${inv.title || ''}`.trim(),
    });
    setPaidOpen(true);
  };

  const submitIssue = async () => {
    if (!issueInvoice) return;
    const net = bexioNetTotal(issueInvoice);
    if (net <= 0) {
      toast.error('Bexio net total is missing or zero; cannot log.');
      return;
    }
    const payload = { tranche_type: issueForm.tranche_type || 'final', amount_chf: net };
    setBusy(true);
    try {
      await api.issueBexioInvoice(deal.id, issueInvoice.id, payload);
      setIssueOpen(false);
      setIssueInvoice(null);
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      toast.success('Recorded as Issued in Bexio (app ledger)');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not log invoice as issued');
    } finally {
      setBusy(false);
    }
  };

  const submitPaid = async () => {
    if (!paidInvoice) return;
    const amount = bexioPaidLogAmount(paidInvoice);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('No amount to log (check Bexio net total).');
      return;
    }
    const prior = payments.filter((p) => extractBexioInvoiceIdFromNote(p.note) === paidInvoice.id);
    if (prior.length) {
      const sum = prior.reduce((a, p) => a + Number(p.amount_chf || 0), 0);
      const ok = window.confirm(
        `This deal already has ${prior.length} payment(s) referencing Bexio invoice #${paidInvoice.id} (${money(sum)} CHF total). Log another payment anyway?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const paidPayload = {
        amount_chf: amount,
        note: (paidForm.note || '').trim(),
      };
      if (paidForm.payment_date) {
        paidPayload.payment_date = paidForm.payment_date;
      }
      await api.markBexioInvoicePaid(deal.id, paidInvoice.id, paidPayload);
      setPaidOpen(false);
      setPaidInvoice(null);
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      if (onDealReload) await onDealReload();
      toast.success('Paid in Bexio — payment logged in ledger');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not log payment');
    } finally {
      setBusy(false);
    }
  };

  const deletePayment = async (paymentId) => {
    setBusy(true);
    try {
      await api.deleteArPayment(paymentId);
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      toast.success('Payment removed');
    } catch {
      toast.error('Could not remove payment');
    } finally {
      setBusy(false);
    }
  };

  const deleteIssuedBexioEngagement = async (inv) => {
    const trancheId = inv.app_tranche_id;
    if (!trancheId) {
      toast.error('No local engagement id for this row; refresh and try again.');
      return;
    }
    const linked = paymentsForTranche(trancheId, payments);
    if (linked.length > 0) {
      toast.error(
        'This invoice has payment ledger entries. Delete those payments first, then you can remove the engaged document.'
      );
      return;
    }
    const label = inv.document_nr || `Invoice #${inv.id}`;
    if (!window.confirm(`Remove "${label}" from the issued ledger? This only affects your app, not Bexio.`)) return;
    setBusy(true);
    try {
      await api.deleteTranche(trancheId);
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      toast.success('Engaged document removed from ledger');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove engaged document');
    } finally {
      setBusy(false);
    }
  };

  const submitManualPayment = async () => {
    const trancheId = String(manualForm.tranche_id || '').trim();
    const amount = Number(String(manualForm.amount_chf).replace(/'/g, '').replace(',', '.'));
    if (!trancheId) {
      toast.error('Select a tranche to post payment.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid payment amount > 0.');
      return;
    }
    const payload = {
      amount_chf: amount,
      payment_date: manualForm.payment_date || todayDateInput(),
      note: (manualForm.note || '').trim(),
      ledger_source: 'manual',
      source_amount: amount,
      source_currency: 'CHF',
    };
    setBusy(true);
    try {
      await api.addTranchePayment(trancheId, payload);
      setManualForm((f) => ({ ...f, amount_chf: '', payment_date: todayDateInput(), note: '' }));
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      if (onDealReload) await onDealReload();
      toast.success('Manual payment added to ledger');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add manual payment');
    } finally {
      setBusy(false);
    }
  };

  const postIssuedAsPaid = async (inv) => {
    const trancheId = inv?.app_tranche_id;
    const row = trancheById.get(trancheId);
    if (!trancheId || !row) {
      toast.error('No linked tranche found for this invoice.');
      return;
    }
    const outstanding = Number(row.outstanding_amount_chf || 0);
    if (outstanding <= 0.005) {
      toast.error('This issued row is already fully paid.');
      return;
    }
    const sourceAmount = Number(row.source_amount ?? outstanding);
    const sourceCurrency = row.source_currency || 'CHF';
    const confirmLabel = inv.document_nr || `Invoice #${inv.id}`;
    const ok = window.confirm(
      `Mark "${confirmLabel}" as paid in payment ledger?\nThis will post ${money(outstanding)} CHF and deduct AR outstanding accordingly.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const postedChf = outstanding;
      await api.addTranchePayment(trancheId, {
        amount_chf: outstanding,
        payment_date: todayDateInput(),
        note: `Issued ledger paid action · ${confirmLabel}`,
        ledger_source: 'manual',
        source_amount: sourceAmount,
        source_currency: sourceCurrency,
      });
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      if (onDealReload) await onDealReload();
      toast.success(`Paid posted: ${money(postedChf)} CHF · AR updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not post payment from issued ledger');
    } finally {
      setBusy(false);
    }
  };

  const openTrancheFx = (inv) => {
    const trancheId = inv?.app_tranche_id;
    const row = trancheById.get(trancheId);
    if (!trancheId || !row) {
      toast.error('No linked tranche found for FX update.');
      return;
    }
    setTrancheFxTarget({
      tranche_id: trancheId,
      label: inv.document_nr || `Invoice #${inv.id}`,
      amount_chf: Number(row.amount_chf || 0),
      source_amount: Number(row.source_amount ?? row.amount_chf ?? 0),
      source_currency: row.source_currency || 'CHF',
    });
    setTrancheFxForm({
      source_currency: row.source_currency || 'CHF',
      source_amount: String(Number(row.source_amount ?? row.amount_chf ?? 0) || ''),
      reason: '',
    });
    setTrancheFxPreview(null);
    setTrancheFxOpen(true);
  };

  const openPaymentFx = (p) => {
    setPaymentFxTarget({
      payment_id: p.payment_id,
      amount_chf: Number(p.amount_chf || 0),
      source_amount: Number(p.source_amount ?? p.amount_chf ?? 0),
      source_currency: p.source_currency || 'CHF',
    });
    setPaymentFxForm({
      source_currency: p.source_currency || 'CHF',
      source_amount: String(Number(p.source_amount ?? p.amount_chf ?? 0) || ''),
      reason: '',
    });
    setPaymentFxPreview(null);
    setPaymentFxOpen(true);
  };

  useEffect(() => {
    if (!trancheFxOpen || !trancheFxTarget?.tranche_id) {
      setTrancheFxPreview(null);
      setTrancheFxPreviewLoading(false);
      return;
    }
    const sourceAmount = Number(String(trancheFxForm.source_amount).replace(/'/g, '').replace(',', '.'));
    if (!Number.isFinite(sourceAmount) || sourceAmount <= 0) {
      setTrancheFxPreview(null);
      setTrancheFxPreviewLoading(false);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setTrancheFxPreviewLoading(true);
      try {
        const res = await api.previewTrancheFx(trancheFxTarget.tranche_id, {
          source_currency: trancheFxForm.source_currency,
          source_amount: sourceAmount,
        });
        if (active) setTrancheFxPreview(res.data);
      } catch {
        if (active) setTrancheFxPreview(null);
      } finally {
        if (active) setTrancheFxPreviewLoading(false);
      }
    }, 220);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [trancheFxOpen, trancheFxTarget?.tranche_id, trancheFxForm.source_currency, trancheFxForm.source_amount]);

  useEffect(() => {
    if (!paymentFxOpen || !paymentFxTarget?.payment_id) {
      setPaymentFxPreview(null);
      setPaymentFxPreviewLoading(false);
      return;
    }
    const sourceAmount = Number(String(paymentFxForm.source_amount).replace(/'/g, '').replace(',', '.'));
    if (!Number.isFinite(sourceAmount) || sourceAmount <= 0) {
      setPaymentFxPreview(null);
      setPaymentFxPreviewLoading(false);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setPaymentFxPreviewLoading(true);
      try {
        const res = await api.previewPaymentFx(paymentFxTarget.payment_id, {
          source_currency: paymentFxForm.source_currency,
          source_amount: sourceAmount,
        });
        if (active) setPaymentFxPreview(res.data);
      } catch {
        if (active) setPaymentFxPreview(null);
      } finally {
        if (active) setPaymentFxPreviewLoading(false);
      }
    }, 220);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [paymentFxOpen, paymentFxTarget?.payment_id, paymentFxForm.source_currency, paymentFxForm.source_amount]);

  const submitTrancheFx = async () => {
    if (!trancheFxTarget?.tranche_id) return;
    const sourceAmount = Number(String(trancheFxForm.source_amount).replace(/'/g, '').replace(',', '.'));
    if (!Number.isFinite(sourceAmount) || sourceAmount <= 0) {
      toast.error('Enter a valid source amount > 0.');
      return;
    }
    setBusy(true);
    try {
      await api.revalueTrancheFx(trancheFxTarget.tranche_id, {
        source_currency: trancheFxForm.source_currency,
        source_amount: sourceAmount,
        reason: (trancheFxForm.reason || '').trim(),
      });
      setTrancheFxOpen(false);
      setTrancheFxTarget(null);
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      toast.success('Issued ledger FX updated and AR recalculated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not revalue issued amount');
    } finally {
      setBusy(false);
    }
  };

  const submitPaymentFx = async () => {
    if (!paymentFxTarget?.payment_id) return;
    const sourceAmount = Number(String(paymentFxForm.source_amount).replace(/'/g, '').replace(',', '.'));
    if (!Number.isFinite(sourceAmount) || sourceAmount <= 0) {
      toast.error('Enter a valid source amount > 0.');
      return;
    }
    setBusy(true);
    try {
      await api.revaluePaymentFx(paymentFxTarget.payment_id, {
        source_currency: paymentFxForm.source_currency,
        source_amount: sourceAmount,
        reason: (paymentFxForm.reason || '').trim(),
      });
      setPaymentFxOpen(false);
      setPaymentFxTarget(null);
      await Promise.all([refreshPayments(), refreshTranches(), refreshFxAudit(), loadBexioInvoices()]);
      toast.success('Payment FX updated and AR deduction recalculated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not revalue payment');
    } finally {
      setBusy(false);
    }
  };

  if (!deal) {
    return <div className="p-6 text-sm text-gray-500">Load a deal to manage Bexio invoices and payments.</div>;
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3 md:p-4">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3">
        <section className="rounded-xl border border-gray-200 bg-white p-3 md:p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Bexio invoices</h3>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-800 disabled:opacity-50"
              disabled={loading || busy || bexioLoading || !deal?.id}
              onClick={() => loadBexioInvoices()}
            >
              Refresh
            </button>
          </div>
          {bexioLoading && !bexioPayload ? (
            <p className="text-xs text-gray-500">Loading Bexio...</p>
          ) : !bexioPayload ? (
            <p className="text-xs text-gray-500">Bexio data not available.</p>
          ) : bexioPayload.match_state === 'no_token' ? (
            <p className="text-xs text-amber-800">{bexioPayload.message || 'Bexio not configured on server.'}</p>
          ) : bexioPayload.match_state === 'no_client' ? (
            <p className="text-xs text-gray-600">{bexioPayload.message || 'Set CLIENT on the deal to load Bexio invoices.'}</p>
          ) : bexioPayload.match_state === 'no_contacts' ? (
            <p className="text-xs text-gray-600">{bexioPayload.message || 'No matching Bexio contact.'}</p>
          ) : bexioPayload.match_state === 'ambiguous_contacts' ? (
            <div className="text-xs text-amber-900">
              <p className="mb-2 font-medium">{bexioPayload.message}</p>
              <ul className="list-inside list-disc text-gray-700">
                {(bexioPayload.ambiguous_contacts || []).map((c) => (
                  <li key={c.id}>{c.label} (id {c.id})</li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="mb-2 text-xs text-gray-600">
                Matched contact <span className="font-semibold text-gray-900">{bexioPayload.bexio_contact?.label}</span>
                {' · '}
                {(bexioPayload.invoices || []).length} invoice(s). Bexio stays the source of truth; this app only logs issued/paid state in CHF ledger.
              </p>
              <label className="mb-3 flex max-w-md flex-col gap-1 text-xs text-gray-600">
                Filter by document nr., title, or id
                <input
                  type="search"
                  className="rounded border px-2 py-1.5 text-sm"
                  value={bexioInvoiceFilter}
                  onChange={(e) => setBexioInvoiceFilter(e.target.value)}
                  placeholder="e.g. RE-, project title, or 1234"
                  disabled={loading || busy}
                />
              </label>
              <div className="space-y-2 md:hidden">
                {(bexioPayload.invoices || []).length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                    No invoices for this contact in Bexio.
                  </div>
                ) : bexioInvoicesFiltered.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                    No invoices match this filter.
                  </div>
                ) : (
                  bexioInvoicesFiltered.map((inv) => (
                    <article key={`mobile-${inv.id}`} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-mono text-xs text-gray-500">{inv.document_nr || `#${inv.id}`}</p>
                          <p className="text-sm font-semibold text-gray-900">{inv.title || '—'}</p>
                        </div>
                        <span className="rounded bg-gray-100 px-2 py-1 text-[11px] text-gray-700 capitalize">
                          {String(inv.app_state || 'not_logged').replace('_', ' ')}
                        </span>
                      </div>
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <dt className="text-gray-400">Currency</dt>
                          <dd className="text-gray-800">{inv.currency_code || (inv.currency_id != null && inv.currency_id !== '' ? `id ${inv.currency_id}` : '—')}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Bexio status</dt>
                          <dd className="text-gray-800">{inv.status_label || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Net total</dt>
                          <dd className="font-mono text-gray-900">{money(bexioNetTotal(inv))} {inv.currency_code || 'CHF'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Remaining</dt>
                          <dd className="font-mono text-gray-900">{money(inv.total_remaining_payments)} {inv.currency_code || 'CHF'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Valid from</dt>
                          <dd className="text-gray-800">{parseBexioDateField(inv.is_valid_from) || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-400">Due</dt>
                          <dd className="text-gray-800">{bexioDueDate(inv) || '—'}</dd>
                        </div>
                      </dl>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          className="rounded border border-violet-300 px-2 py-2 text-xs font-medium text-violet-900 disabled:opacity-40"
                          disabled={loading || busy}
                          onClick={() => openIssueModal(inv)}
                        >
                          Issued in Bexio
                        </button>
                        <button
                          type="button"
                          className="rounded border border-sky-300 px-2 py-2 text-xs font-medium text-sky-800 disabled:opacity-40"
                          disabled={loading || busy}
                          onClick={() => openPaidModal(inv)}
                        >
                          Paid in Bexio
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-[12px]">
                  <thead>
                    <tr className="border-b bg-gray-50 text-xs font-medium tracking-wide text-gray-500">
                      <th className="px-2.5 py-1.5 text-left">Document</th>
                      <th className="px-2.5 py-1.5 text-left">Invoice Title</th>
                      <th className="px-2.5 py-1.5 text-center">Currency</th>
                      <th className="px-2.5 py-1.5 text-center">Bexio status</th>
                      <th className="px-2.5 py-1.5 text-center">In app</th>
                      <th className="px-2.5 py-1.5 text-right">Net total</th>
                      <th className="px-2.5 py-1.5 text-right">Remaining</th>
                      <th className="px-2.5 py-1.5 text-center">Valid from</th>
                      <th className="px-2.5 py-1.5 text-center">Due / valid to</th>
                      <th className="px-2.5 py-1.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bexioPayload.invoices || []).length === 0 ? (
                      <tr>
                        <td className="px-3 py-3 text-gray-500" colSpan={10}>
                          No invoices for this contact in Bexio.
                        </td>
                      </tr>
                    ) : bexioInvoicesFiltered.length === 0 ? (
                      <tr>
                        <td className="px-3 py-3 text-gray-500" colSpan={10}>
                          No invoices match this filter.
                        </td>
                      </tr>
                    ) : (
                      bexioInvoicesFiltered.map((inv) => (
                        <tr key={inv.id} className="border-b">
                          <td className="px-2.5 py-1.5 font-mono text-xs">{inv.document_nr || `#${inv.id}`}</td>
                          <td className="px-2.5 py-1.5 text-gray-700">{inv.title || '—'}</td>
                          <td className="px-2.5 py-1.5 text-center">
                            {inv.currency_code || (inv.currency_id != null && inv.currency_id !== '' ? `id ${inv.currency_id}` : '—')}
                          </td>
                          <td className="px-2.5 py-1.5 text-center">{inv.status_label || '—'}</td>
                          <td className="px-2.5 py-1.5 text-center capitalize">{String(inv.app_state || 'not_logged').replace('_', ' ')}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums">{money(bexioNetTotal(inv))} {inv.currency_code || 'CHF'}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums">{money(inv.total_remaining_payments)} {inv.currency_code || 'CHF'}</td>
                          <td className="px-2.5 py-1.5 text-center">{parseBexioDateField(inv.is_valid_from) || '—'}</td>
                          <td className="px-2.5 py-1.5 text-center">{bexioDueDate(inv) || '—'}</td>
                          <td className="px-2.5 py-1.5 text-right">
                            <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap">
                              <button
                                type="button"
                                className="rounded border border-violet-300 px-2 py-1 text-xs text-violet-900 disabled:opacity-40"
                                disabled={loading || busy}
                                onClick={() => openIssueModal(inv)}
                              >
                                Issued in Bexio
                              </button>
                              <button
                                type="button"
                                className="rounded border border-sky-300 px-2 py-1 text-xs text-sky-800 disabled:opacity-40"
                                disabled={loading || busy}
                                onClick={() => openPaidModal(inv)}
                              >
                                Paid in Bexio
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-3 md:p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Issued ledger (Bexio-engaged)</h3>
          <p className="mb-3 text-xs text-gray-500">
            Documents appear here after you press <span className="font-semibold">Issued in Bexio</span> (or
            <span className="font-semibold"> Paid in Bexio</span>, which also records issuance first). When Bexio
            status becomes <span className="font-semibold">Paid</span>, Refresh (or reopening this tab) syncs remaining
            outstanding into the payment ledger automatically — no Paid CTA required. Use{' '}
            <span className="font-semibold">Paid</span> only if you need to log payment before Bexio shows Paid. Use{' '}
            <span className="font-semibold">Delete</span> to remove an engagement that has no payments yet; if there are
            payments, delete them in the payment ledger first. Net total shows the local posted CHF amount; rows with FX edits are labeled.
          </p>
          <div className="space-y-2 md:hidden">
            {issuedLedgerRows.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                No engaged documents yet.
              </div>
            ) : issuedLedgerRows.map((inv) => (
              <article key={`issued-mobile-${inv.id}`} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-xs text-gray-500">{inv.document_nr || `#${inv.id}`}</p>
                    <p className="text-sm font-semibold text-gray-900">{inv.title || '-'}</p>
                  </div>
                  <span className="rounded bg-gray-100 px-2 py-1 text-[11px] text-gray-700 capitalize">
                    {String(inv.app_state || '').replace('_', ' ') || '—'}
                  </span>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="text-gray-400">Net total</dt>
                    <dd className="font-mono text-gray-900">
                      {money(issuedLedgerDisplay(inv).amount)} {issuedLedgerDisplay(inv).currency}
                    </dd>
                    {issuedLedgerDisplay(inv).fxAdjusted ? (
                      <dd className="mt-1 text-[11px] text-violet-700">FX adjusted from {issuedLedgerDisplay(inv).sourceCurrency}</dd>
                    ) : null}
                  </div>
                  <div>
                    <dt className="text-gray-400">Outstanding</dt>
                    <dd className="font-mono text-gray-900">{money(issuedLedgerDisplay(inv).outstandingChf)} CHF</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">Issue date</dt>
                    <dd className="text-gray-800">{parseBexioDateField(inv.is_valid_from) || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">Due date</dt>
                    <dd className="text-gray-800">{bexioDueDate(inv) || '—'}</dd>
                  </div>
                        <div>
                          <dt className="text-gray-400">Ledger source currency</dt>
                          <dd className="text-gray-800">{trancheById.get(inv.app_tranche_id)?.source_currency || 'CHF'}</dd>
                        </div>
                </dl>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    className="w-full rounded border border-emerald-300 px-2 py-2 text-xs font-medium text-emerald-800 disabled:opacity-40"
                    disabled={busy || loading || !inv.app_tranche_id || issuedLedgerDisplay(inv).outstandingChf <= 0.005}
                    onClick={() => postIssuedAsPaid(inv)}
                  >
                    Paid
                  </button>
                  <button
                    type="button"
                    className="w-full rounded border border-slate-300 px-2 py-2 text-xs font-medium text-slate-700 disabled:opacity-40"
                    disabled={busy || loading || !inv.app_tranche_id}
                    onClick={() => openTrancheFx(inv)}
                  >
                    FX currency
                  </button>
                  <button
                    type="button"
                    className="w-full rounded border border-red-300 px-2 py-2 text-xs font-medium text-red-700 disabled:opacity-40"
                    disabled={busy || loading || !inv.app_tranche_id}
                    onClick={() => deleteIssuedBexioEngagement(inv)}
                  >
                    Delete engagement
                  </button>
                </div>
              </article>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-[12px]">
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-medium tracking-wide text-gray-500">
                  <th className="px-3 py-2 text-left">Document</th>
                  <th className="px-3 py-2 text-left">Invoice Title</th>
                  <th className="px-3 py-2 text-right">Net total</th>
                  <th className="px-3 py-2 text-right">Outstanding</th>
                  <th className="px-3 py-2 text-center">Issue date</th>
                  <th className="px-3 py-2 text-center">Due date</th>
                  <th className="px-3 py-2 text-center">In app</th>
                  <th className="px-3 py-2 text-center">Ledger source ccy</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {issuedLedgerRows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={9}>
                      No engaged documents yet.
                    </td>
                  </tr>
                ) : issuedLedgerRows.map((inv) => (
                  <tr key={`issued-${inv.id}`} className="border-b">
                    <td className="px-3 py-2 font-mono text-xs">{inv.document_nr || `#${inv.id}`}</td>
                    <td className="px-3 py-2 text-gray-700">{inv.title || '-'}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {money(issuedLedgerDisplay(inv).amount)} {issuedLedgerDisplay(inv).currency}
                      {issuedLedgerDisplay(inv).fxAdjusted ? (
                        <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800">
                          FX adjusted ({issuedLedgerDisplay(inv).sourceCurrency})
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(issuedLedgerDisplay(inv).outstandingChf)} CHF</td>
                    <td className="px-3 py-2 text-center">{parseBexioDateField(inv.is_valid_from) || '—'}</td>
                    <td className="px-3 py-2 text-center">{bexioDueDate(inv) || '—'}</td>
                    <td className="px-3 py-2 text-center capitalize">{String(inv.app_state || '').replace('_', ' ')}</td>
                    <td className="px-3 py-2 text-center">{trancheById.get(inv.app_tranche_id)?.source_currency || 'CHF'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <button
                          type="button"
                          className="rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-800 disabled:opacity-40"
                          disabled={busy || loading || !inv.app_tranche_id || issuedLedgerDisplay(inv).outstandingChf <= 0.005}
                          onClick={() => postIssuedAsPaid(inv)}
                        >
                          Paid
                        </button>
                        <button
                          type="button"
                          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 disabled:opacity-40"
                          disabled={busy || loading || !inv.app_tranche_id}
                          onClick={() => openTrancheFx(inv)}
                        >
                          FX currency
                        </button>
                        <button
                          type="button"
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 disabled:opacity-40"
                          disabled={busy || loading || !inv.app_tranche_id}
                          onClick={() => deleteIssuedBexioEngagement(inv)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-3 md:p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Payment ledger</h3>
          <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="mb-2 text-xs text-gray-600">
              Manual payment entry (no Bexio required). Posting here immediately deducts AR outstanding through the ledger formula.
            </p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
              <label className="flex flex-col gap-1 text-xs text-gray-600 md:col-span-2">
                Issued tranche
                <select
                  className="rounded border px-2 py-2 text-sm"
                  value={manualForm.tranche_id}
                  onChange={(e) => setManualForm((f) => ({ ...f, tranche_id: e.target.value }))}
                  disabled={busy || loading || manualPaymentTranches.length === 0}
                >
                  <option value="">Select tranche...</option>
                  {manualPaymentTranches.map((t) => (
                    <option key={t.tranche_id} value={t.tranche_id}>
                      {String(t.tranche_type || '').toUpperCase()} · Outstanding {money(t.outstanding_amount_chf)} CHF
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Amount (CHF)
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={manualForm.amount_chf}
                  onChange={(e) => setManualForm((f) => ({ ...f, amount_chf: e.target.value }))}
                  placeholder="0.00"
                  disabled={busy || loading}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Date
                <input
                  className="rounded border px-2 py-2 text-sm"
                  type="date"
                  value={manualForm.payment_date}
                  onChange={(e) => setManualForm((f) => ({ ...f, payment_date: e.target.value }))}
                  disabled={busy || loading}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Note
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={manualForm.note}
                  onChange={(e) => setManualForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="optional"
                  disabled={busy || loading}
                />
              </label>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                className="rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                disabled={busy || loading || !manualForm.tranche_id}
                onClick={submitManualPayment}
              >
                Add manual payment
              </button>
            </div>
          </div>
          <div className="space-y-2 md:hidden">
            {payments.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                No payments logged.
              </div>
            ) : payments.map((p) => (
              <article key={`pay-mobile-${p.payment_id}`} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-xs text-gray-500">{toDateInput(p.payment_date)}</p>
                    <p className="text-sm font-semibold text-gray-900 capitalize">{p.tranche_type}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-700"
                      type="button"
                      disabled={busy || loading}
                      onClick={() => openPaymentFx(p)}
                    >
                      FX
                    </button>
                    <button
                      className="rounded border border-red-300 px-2 py-1.5 text-xs text-red-700"
                      type="button"
                      disabled={busy || loading}
                      onClick={() => deletePayment(p.payment_id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <dl className="mt-2 grid grid-cols-1 gap-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-400">Amount</dt>
                    <dd className="font-mono text-gray-900">{money(p.amount_chf)} CHF</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-400">Payment currency</dt>
                    <dd className="text-gray-700">{p.source_currency || 'CHF'}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-400">Source amount</dt>
                    <dd className="font-mono text-gray-900">{money(p.source_amount ?? p.amount_chf)} {p.source_currency || 'CHF'}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-400">Source</dt>
                    <dd className="capitalize text-gray-700">{p.ledger_source || 'manual'}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-gray-400">Note</dt>
                    <dd className="text-right text-gray-700">{p.note || '-'}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-[12px]">
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-medium tracking-wide text-gray-500">
                  <th className="px-3 py-2 text-center">Date</th>
                  <th className="px-3 py-2 text-left">Tranche</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Source amount</th>
                  <th className="px-3 py-2 text-center">Payment ccy</th>
                  <th className="px-3 py-2 text-center">Source</th>
                  <th className="px-3 py-2 text-left">Note</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={8}>No payments logged.</td></tr>
                ) : payments.map((p) => (
                  <tr key={p.payment_id} className="border-b">
                    <td className="px-3 py-2 text-center">{toDateInput(p.payment_date)}</td>
                    <td className="px-3 py-2 capitalize">{p.tranche_type}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(p.amount_chf)} CHF</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(p.source_amount ?? p.amount_chf)} {p.source_currency || 'CHF'}</td>
                    <td className="px-3 py-2 text-center">{p.source_currency || 'CHF'}</td>
                    <td className="px-3 py-2 text-center text-gray-700 capitalize">{p.ledger_source || 'manual'}</td>
                    <td className="px-3 py-2 text-gray-700">{p.note || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700"
                          type="button"
                          disabled={busy || loading}
                          onClick={() => openPaymentFx(p)}
                        >
                          FX currency
                        </button>
                        <button
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                          type="button"
                          disabled={busy || loading}
                          onClick={() => deletePayment(p.payment_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-3 md:p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">FX audit trail</h3>
          <p className="mb-3 text-xs text-gray-500">
            Every FX revaluation stores before/after CHF amounts and conversion formula for audit.
          </p>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-[12px]">
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-medium tracking-wide text-gray-500">
                  <th className="px-3 py-2 text-center">When</th>
                  <th className="px-3 py-2 text-center">Target</th>
                  <th className="px-3 py-2 text-left">Formula</th>
                  <th className="px-3 py-2 text-right">Before CHF</th>
                  <th className="px-3 py-2 text-right">After CHF</th>
                  <th className="px-3 py-2 text-right">Delta CHF</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {fxAuditRows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={7}>No FX audit entries yet.</td>
                  </tr>
                ) : fxAuditRows.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="px-3 py-2 text-center">{toDateInput(row.created_at)}</td>
                    <td className="px-3 py-2 text-center capitalize">{row.target_type}</td>
                    <td className="px-3 py-2 font-mono text-gray-700">{row.formula}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(row.pre_amount_chf)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(row.post_amount_chf)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(row.delta_amount_chf)}</td>
                    <td className="px-3 py-2 text-gray-700">{row.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2 md:hidden">
            {fxAuditRows.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                No FX audit entries yet.
              </div>
            ) : fxAuditRows.map((row) => (
              <article key={`fx-audit-${row.id}`} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <p className="text-xs text-gray-500">{toDateInput(row.created_at)} · <span className="capitalize">{row.target_type}</span></p>
                <p className="mt-1 font-mono text-xs text-gray-700">{row.formula}</p>
                <p className="mt-1 text-xs text-gray-700">
                  Before {money(row.pre_amount_chf)} CHF · After {money(row.post_amount_chf)} CHF · Delta {money(row.delta_amount_chf)} CHF
                </p>
                <p className="mt-1 text-xs text-gray-600">{row.reason || '-'}</p>
              </article>
            ))}
          </div>
        </section>

        <Dialog
          open={issueOpen}
          onOpenChange={(open) => {
            setIssueOpen(open);
            if (!open) setIssueInvoice(null);
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Issued in Bexio</DialogTitle>
              <DialogDescription>
                {issueInvoice ? `${issueInvoice.document_nr || `Invoice #${issueInvoice.id}`} · ${issueInvoice.status_label || 'Status unknown'}` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 text-sm">
              {issueInvoice ? (
                <p className="text-sm text-gray-800">
                  <span className="text-xs text-gray-500">Amount (Bexio net)</span>
                  <br />
                  <span className="font-mono text-base font-semibold tabular-nums">
                    {money(bexioNetTotal(issueInvoice))}{' '}
                    <span className="text-sm font-normal text-gray-600">{issueInvoice.currency_code || ''}</span>
                  </span>
                </p>
              ) : null}
              {issueInvoice && !isBexioInvoiceChf(issueInvoice) ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  Invoice currency is not CHF; the same Bexio net figure is stored on the local ledger line (no FX conversion).
                </div>
              ) : null}
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Local tranche bucket
                <select
                  className="rounded border border-gray-300 px-2 py-2 text-sm text-gray-900"
                  value={issueForm.tranche_type}
                  onChange={(e) => setIssueForm((f) => ({ ...f, tranche_type: e.target.value }))}
                >
                  <option value="upfront">Upfront</option>
                  <option value="final">Final</option>
                </select>
              </label>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
                onClick={() => {
                  setIssueOpen(false);
                  setIssueInvoice(null);
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={busy}
                onClick={submitIssue}
              >
                Confirm
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={paidOpen}
          onOpenChange={(open) => {
            setPaidOpen(open);
            if (!open) setPaidInvoice(null);
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Paid in Bexio</DialogTitle>
              <DialogDescription>
                {paidInvoice ? `${paidInvoice.document_nr || `Invoice #${paidInvoice.id}`} · ${paidInvoice.status_label || 'Status unknown'}` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 text-sm">
              {paidInvoice ? (
                <p className="text-sm text-gray-800">
                  <span className="text-xs text-gray-500">Amount (Bexio net)</span>
                  <br />
                  <span className="font-mono text-base font-semibold tabular-nums">
                    {money(bexioPaidLogAmount(paidInvoice))}{' '}
                    <span className="text-sm font-normal text-gray-600">{paidInvoice.currency_code || ''}</span>
                  </span>
                </p>
              ) : null}
              {paidInvoice && !isBexioInvoiceChf(paidInvoice) ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  Invoice currency is not CHF; the same Bexio figure is posted to the local ledger (no FX conversion).
                </div>
              ) : null}
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Payment date (optional)
                <input
                  className="rounded border px-2 py-2 text-sm"
                  type="date"
                  value={paidForm.payment_date}
                  onChange={(e) => setPaidForm((f) => ({ ...f, payment_date: e.target.value }))}
                />
                <span className="text-[11px] font-normal text-gray-500">
                  Leave empty to use the date from Bexio when the API includes one; otherwise today (UTC).
                </span>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Note
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={paidForm.note}
                  onChange={(e) => setPaidForm((f) => ({ ...f, note: e.target.value }))}
                />
              </label>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
                onClick={() => {
                  setPaidOpen(false);
                  setPaidInvoice(null);
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={busy || bexioPaidLogAmount(paidInvoice) <= 0}
                onClick={submitPaid}
              >
                Confirm
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={trancheFxOpen}
          onOpenChange={(open) => {
            setTrancheFxOpen(open);
            if (!open) {
              setTrancheFxTarget(null);
              setTrancheFxPreview(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Issued ledger FX currency</DialogTitle>
              <DialogDescription>
                {trancheFxTarget ? `${trancheFxTarget.label} · Current ${money(trancheFxTarget.amount_chf)} CHF` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 text-sm">
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Source currency (currency of the original invoice figure)
                <select
                  className="rounded border px-2 py-2 text-sm"
                  value={trancheFxForm.source_currency}
                  onChange={(e) => setTrancheFxForm((f) => ({ ...f, source_currency: e.target.value }))}
                >
                  {CURRENCY_OPTIONS.map((ccy) => (
                    <option key={ccy} value={ccy}>{ccy}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Source amount
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={trancheFxForm.source_amount}
                  onChange={(e) => setTrancheFxForm((f) => ({ ...f, source_amount: e.target.value }))}
                  placeholder="e.g. 9135"
                />
                <span className="text-[11px] text-gray-500">
                  System converts this amount to CHF using current/locked FX and updates AR issued amount.
                </span>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Reason (optional, stored in audit)
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={trancheFxForm.reason}
                  onChange={(e) => setTrancheFxForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Bexio currency mismatch corrected"
                />
              </label>
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                {trancheFxPreviewLoading ? (
                  <span>Calculating preview...</span>
                ) : trancheFxPreview ? (
                  <div className="space-y-1">
                    <p className="font-mono">{trancheFxPreview.formula}</p>
                    <p>
                      Before: <span className="font-mono">{money(trancheFxPreview.pre_amount_chf)} CHF</span>
                      {' · '}
                      After: <span className="font-mono">{money(trancheFxPreview.post_amount_chf)} CHF</span>
                      {' · '}
                      Delta: <span className="font-mono">{money(trancheFxPreview.delta_amount_chf)} CHF</span>
                    </p>
                  </div>
                ) : (
                  <span>Enter source amount/currency to preview conversion before save.</span>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
                onClick={() => {
                  setTrancheFxOpen(false);
                  setTrancheFxTarget(null);
                  setTrancheFxPreview(null);
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={busy}
                onClick={submitTrancheFx}
              >
                Apply FX
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={paymentFxOpen}
          onOpenChange={(open) => {
            setPaymentFxOpen(open);
            if (!open) {
              setPaymentFxTarget(null);
              setPaymentFxPreview(null);
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Payment ledger FX currency</DialogTitle>
              <DialogDescription>
                {paymentFxTarget ? `Current deduction ${money(paymentFxTarget.amount_chf)} CHF` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 text-sm">
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Payment currency
                <select
                  className="rounded border px-2 py-2 text-sm"
                  value={paymentFxForm.source_currency}
                  onChange={(e) => setPaymentFxForm((f) => ({ ...f, source_currency: e.target.value }))}
                >
                  {CURRENCY_OPTIONS.map((ccy) => (
                    <option key={ccy} value={ccy}>{ccy}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Source amount
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={paymentFxForm.source_amount}
                  onChange={(e) => setPaymentFxForm((f) => ({ ...f, source_amount: e.target.value }))}
                  placeholder="e.g. 9135"
                />
                <span className="text-[11px] text-gray-500">
                  System converts to CHF and updates the AR deduction from this payment.
                </span>
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                Reason (optional, stored in audit)
                <input
                  className="rounded border px-2 py-2 text-sm"
                  value={paymentFxForm.reason}
                  onChange={(e) => setPaymentFxForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Payment was in USD"
                />
              </label>
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                {paymentFxPreviewLoading ? (
                  <span>Calculating preview...</span>
                ) : paymentFxPreview ? (
                  <div className="space-y-1">
                    <p className="font-mono">{paymentFxPreview.formula}</p>
                    <p>
                      Before: <span className="font-mono">{money(paymentFxPreview.pre_amount_chf)} CHF</span>
                      {' · '}
                      After: <span className="font-mono">{money(paymentFxPreview.post_amount_chf)} CHF</span>
                      {' · '}
                      Delta: <span className="font-mono">{money(paymentFxPreview.delta_amount_chf)} CHF</span>
                    </p>
                  </div>
                ) : (
                  <span>Enter source amount/currency to preview conversion before save.</span>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800"
                onClick={() => {
                  setPaymentFxOpen(false);
                  setPaymentFxTarget(null);
                  setPaymentFxPreview(null);
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={busy}
                onClick={submitPaymentFx}
              >
                Apply FX
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
